import { Link } from 'react-router-dom';
import { SavedQuery } from '../../services/savedQueries.service';
import './SavedQueriesSidebar.css';

interface SavedQueriesSidebarProps {
  queries: SavedQuery[];
  isOpen: boolean;
  onToggle: () => void;
  onSelectQuery: (query: SavedQuery) => void;
  _onExecuteQuery: (query: SavedQuery) => void;
  onDeleteQuery: (id: string) => void;
  isLoading: boolean;
}

/**
 * Sidebar displaying saved queries
 * Collapsible panel with query list and actions
 */
export const SavedQueriesSidebar = ({
  queries,
  isOpen,
  onToggle,
  onSelectQuery,
  _onExecuteQuery,
  onDeleteQuery,
  isLoading,
}: SavedQueriesSidebarProps) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this saved query?')) {
      onDeleteQuery(id);
    }
  };

  return (
    <>
      <button
        type="button"
        className={`sidebar-toggle ${isOpen ? 'open' : ''}`}
        onClick={onToggle}
        title="Toggle Saved Queries"
        aria-label="Toggle Saved Queries"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
        </svg>
        <span className="sidebar-toggle-text">Saved</span>
        {queries.length > 0 && <span className="query-count">{queries.length}</span>}
      </button>

      <div
        className={`sidebar-overlay ${isOpen ? 'visible' : ''}`}
        onClick={onToggle}
        onKeyDown={(e) => e.key === 'Escape' && onToggle()}
        role="button"
        tabIndex={0}
        aria-label="Close sidebar"
      ></div>

      <div className={`saved-queries-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h3>Saved Queries</h3>
          <button type="button" className="sidebar-close" onClick={onToggle} aria-label="Close">
            ×
          </button>
        </div>

        <div className="sidebar-content">
          {isLoading && (
            <div className="sidebar-loading">
              <div className="loading-spinner"></div>
              <p>Loading queries...</p>
            </div>
          )}

          {!isLoading && queries.length === 0 && (
            <div className="sidebar-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
              </svg>
              <p>No saved queries yet</p>
              <small>Save queries to rerun them later</small>
            </div>
          )}

          {!isLoading && queries.length > 0 && (
            <div className="queries-list">
              {queries.map((query) => (
                <div
                  key={query.id}
                  className="query-item"
                  onClick={() => onSelectQuery(query)}
                  onKeyDown={(e) => e.key === 'Enter' && onSelectQuery(query)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="query-item-header">
                    <h4 className="query-name">{query.name}</h4>
                    <button
                      type="button"
                      className="query-delete"
                      onClick={(e) => handleDelete(e, query.id)}
                      aria-label="Delete query"
                      title="Delete query"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>

                  {query.description && <p className="query-description">{query.description}</p>}

                  <div className="query-preview">{query.query.substring(0, 100)}...</div>

                  <Link
                    to={`/query/${query.id}`}
                    className="query-execute-btn"
                    onClick={(e) => e.stopPropagation()}
                    title="View and execute this query"
                  >
                    👁️ View & Run Query
                  </Link>

                  <div className="query-meta">
                    <span className="datacenter-badge">{query.datacenter}</span>
                    <span className="query-date">
                      {formatDate(query.createdAt)} {formatTime(query.createdAt)}
                    </span>
                  </div>

                  {query.lastExecutedAt && (
                    <div className="query-last-run">
                      Last run: {formatDate(query.lastExecutedAt)} {formatTime(query.lastExecutedAt)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
